/**
 * revia-backoffice — backoffice.revia.to. While the session is still being
 * read it shows the brand alone — never <sign-in>, whose consent screen a
 * staff member's verify would otherwise reach, since staff hold no users
 * record. Signed out (or without the second factor the staff reads require),
 * it mounts Revent's own <sign-in>; a signed-in member without Revia's staff
 * permission is told so and offered another account; staff get Revent's
 * admin shell with the access requests and the managed clients' licences.
 */
import { fill, overflowWithin } from "@mhmo91/schmancy/directives";
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
import "@momo/views";
import { session, signAction$ } from "@momo/views";
import { css, html } from "lit";
import { LocalizeMixin } from "../i18n/localize.ts";
import "../staff/staff-notice.view.ts";
import { staffGate } from "../staff/staff.state.ts";
import "./revia-backoffice-shell.view.ts";

@schmancyElement("revia-backoffice")
export class ReviaBackoffice extends LocalizeMixin(SchmancyElement) {
	// A flex item of the body column, so the theme's fill chain resolves a height.
	static styles = [
		css`
			:host {
				display: flex;
				flex-direction: column;
				flex: 1 1 0;
				min-height: 0;
			}
		`,
	];

	private signOut = () =>
		signAction$.next({ kind: "signOut", requestId: crypto.randomUUID() });

	override render() {
		const gate = staffGate.get();

		return html`
			<schmancy-theme root>
				<session-error></session-error>
				<passkey-offer .enabled=${gate === "staff"} .allowNewUser=${gate === "staff"}></passkey-offer>
				${gate === "unanswered"
					? html`
							<div ${fill()} class="grid">
								<main class="grid content-start justify-items-center gap-6 px-4 pt-eye">
									<schmancy-typography type="title" token="md" weight="bold">${this.t("backoffice.brand")}</schmancy-typography>
									<schmancy-visually-hidden role="status" aria-live="polite">${this.t("backoffice.session.checking")}</schmancy-visually-hidden>
								</main>
							</div>
						`
					: gate === "sign-in"
					? html`
							<div ${fill()} class="grid grid-cols-1 min-w-0 content-center justify-items-center gap-8 p-6">
								<sign-in class="min-w-0 w-full max-w-md" brand-name=${this.t("backoffice.brand")}></sign-in>
							</div>
						`
					: gate === "notice" || gate === "notice-unanswered" || gate === "notice-error"
						? html`<div ${fill()} class="grid"><staff-notice></staff-notice></div>`
						: gate === "not-staff"
						? html`
								<div ${fill()} class="grid">
									<main ${overflowWithin()} class="grid content-start justify-items-center gap-6 px-4 pt-eye">
										<schmancy-typography type="title" token="md" weight="bold">${this.t("backoffice.brand")}</schmancy-typography>
										<div class="grid w-full max-w-md justify-items-start gap-4 text-start">
											<schmancy-typography type="headline" token="sm">${this.t("backoffice.notStaff.title")}</schmancy-typography>
											<schmancy-typography type="body" token="lg" class="text-surface-onVariant">
												${this.t("backoffice.notStaff.signedInAs", { email: session.value.email ?? "" })}
											</schmancy-typography>
											<div>
												<schmancy-button variant="filled" @click=${this.signOut}>
													${this.t("backoffice.notStaff.switch")}
												</schmancy-button>
											</div>
										</div>
									</main>
								</div>
							`
						: html`<revia-backoffice-shell></revia-backoffice-shell>`}
			</schmancy-theme>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"revia-backoffice": ReviaBackoffice;
	}
}
